import * as impl from './impl.js';
import {AuthenticationRequest, AuthenticationResponse} from './types.js';
import {jwtVerifier, type VerifyJwt} from '@interopio/gateway/jose/jwt';

export interface Oauth2AuthenticatorConfig extends impl.DefaultAuthenticatorConfig {
    issuerBaseURL?: string
    audience?: string
}

class Oauth2Authenticator implements impl.AuthImpl {
    readonly #jwtVerifier: VerifyJwt;

    constructor(config: Oauth2AuthenticatorConfig, fetchFn: typeof fetch) {
        this.#jwtVerifier = jwtVerifier({
            issuerBaseUri: config.issuerBaseURL,
            audience: config.audience ? [config.audience] : [],
            clockTolerance: 3600,
            timeout: 5000,
            cacheMaxAge: 60000,
            fetchFn
        });
    }

    async auth(authenticationRequest: AuthenticationRequest): Promise<AuthenticationResponse> {
        const {method, token: accessToken} = authenticationRequest.authentication;
        if (method !== 'access-token') {
            throw new Error(`unsupported authentication method`);
        }
        if (!accessToken) {
            throw new Error('missing access token');
        }

        try {
            const {payload} = await this.#jwtVerifier(accessToken);
            const user = payload.sub;
            return {type: 'success', user};
        }
        catch (e) {
            throw new Error(`invalid token`, {cause: e});
        }
    }
}

export function authenticator(config: Oauth2AuthenticatorConfig, fetchFn: typeof fetch) {
    return impl.authenticator(config, new Oauth2Authenticator(config, fetchFn));
}
